import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionPanel } from '~/components/ui/SectionPanel';
import { formatCurrency } from '~/utils';
import { formatAddressText } from '~/utils/addressFormat';
import type { GetRequestForQuoteWithQuotesQuery } from '~/graphql/graphql';
import { quantityInBundles } from '~/pages/rfq-view/bundleQuantity';
import type { ProductGroup } from '~/pages/rfq-view/itemGroups';
import {
  ProductGroupTable,
  ProductGroupCells,
} from '~/pages/rfq-view/ProductGroupTable';
import { itemsByPlatformProductLengthId, moneyOrDash } from './quoteDisplay';

type RequestForQuoteSummaryProps = {
  requestForQuote: NonNullable<
    GetRequestForQuoteWithQuotesQuery['requestForQuote']
  >;
  groups: ProductGroup[];
  headingKey?: string;
  /** Replaces the ship-to, lead-time and created-by lines; seller pages pass the buyer and vehicle. */
  details?: ReactNode;
};

export function RequestForQuoteSummary({
  requestForQuote,
  groups,
  headingKey = 'rfq_view.your_request_heading',
  details,
}: Readonly<RequestForQuoteSummaryProps>) {
  const { t } = useTranslation('rfq-view');
  const itemByPlatformProductLengthId = itemsByPlatformProductLengthId(
    requestForQuote.items
  );
  const shippingLocation = requestForQuote.shippingLocation;
  const address = shippingLocation?.facility.address;

  return (
    <SectionPanel title={t(headingKey)}>
      <div className="mb-6">
        {details ?? (
          <>
            {shippingLocation && (
              <p className="text-sm text-neut-prim mb-1">
                <span className="font-semibold">
                  {t('rfq_view.ship_to_location_label')}:
                </span>{' '}
                {shippingLocation.code} — {shippingLocation.name}
                {address && `, ${formatAddressText(address)}`}
              </p>
            )}

            {requestForQuote.leadTimeDays != null && (
              <p className="text-sm text-neut-prim mb-1">
                <span className="font-semibold">
                  {t('rfq_view.lead_time_label')}:
                </span>{' '}
                {t('rfq_view.lead_time_days', {
                  count: requestForQuote.leadTimeDays,
                })}
              </p>
            )}

            {requestForQuote.createdByUserName && (
              <p
                className="text-sm text-neut-prim mb-1"
                data-testid="rfq-created-by"
              >
                <span className="font-semibold">
                  {t('rfq_view.created_by_label')}:
                </span>{' '}
                {requestForQuote.createdByUserName}
              </p>
            )}
          </>
        )}

        {requestForQuote.cannedTally && (
          <p
            className="text-sm text-neut-prim mb-1"
            data-testid="rfq-canned-tally"
          >
            <span className="font-semibold">
              {t('rfq_view.canned_tally_label')}:
            </span>{' '}
            {t('rfq_view.canned_tally_name_count', {
              name: requestForQuote.cannedTally.name,
              tallyCount: requestForQuote.cannedTallyCount,
            })}
          </p>
        )}

        {requestForQuote.currency && (
          <p className="text-sm text-neut-prim mb-1">
            <span className="font-semibold">
              {t('rfq_view.currency_label')}:
            </span>{' '}
            {requestForQuote.currency}
          </p>
        )}

        {(requestForQuote.pickupPriceForTally ||
          requestForQuote.deliveryPriceForTally) && (
          <p className="text-sm text-neut-prim mb-3">
            {requestForQuote.pickupPriceForTally && (
              <>
                <span className="font-semibold">
                  {t('rfq_view.whole_request_pickup_price_label')}:
                </span>{' '}
                {formatCurrency(
                  requestForQuote.pickupPriceForTally.amount,
                  requestForQuote.pickupPriceForTally.currency
                )}
              </>
            )}
            {requestForQuote.pickupPriceForTally &&
              requestForQuote.deliveryPriceForTally &&
              '  ·  '}
            {requestForQuote.deliveryPriceForTally && (
              <>
                <span className="font-semibold">
                  {t('rfq_view.whole_request_delivery_price_label')}:
                </span>{' '}
                {formatCurrency(
                  requestForQuote.deliveryPriceForTally.amount,
                  requestForQuote.deliveryPriceForTally.currency
                )}
              </>
            )}
          </p>
        )}
      </div>

      <ProductGroupTable
        groups={groups}
        columnLabels={[
          t('rfq_view.quantity_label'),
          t('rfq_view.pickup_price_label'),
          t('rfq_view.delivery_price_label'),
        ]}
        renderRow={(row) => {
          const item = itemByPlatformProductLengthId.get(
            row.platformProductLengthId
          );
          return (
            <ProductGroupCells
              label={row.displayValue}
              values={[
                {
                  columnKey: 'quantity',
                  value:
                    row.requestedQuantityInPieces == null
                      ? '—'
                      : quantityInBundles(
                          row.requestedQuantityInPieces,
                          row.piecesPerBundle
                        ),
                },
                {
                  columnKey: 'pickupPrice',
                  value: item ? moneyOrDash(item.pickupPrice) : '—',
                },
                {
                  columnKey: 'deliveryPrice',
                  value: item ? moneyOrDash(item.deliveryPrice) : '—',
                },
              ]}
            />
          );
        }}
      />
    </SectionPanel>
  );
}
